import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import FirstStepsModal from './FirstStepsModal';
import OnboardingWizard from './OnboardingWizard';
import TierProgressionWidget from './TierProgressionWidget';
import { 
  Wallet, 
  CreditCard, 
  Users, 
  TrendingUp, 
  Plus,
  Eye,
  Globe,
  Link as LinkIcon,
  RotateCcw,
  Database,
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Crown,
  Star,
  Check,
  Gift,
  GraduationCap,
  Package,
  ShoppingBag
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const MemberDashboard = () => {
  const [memberStats, setMemberStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFirstStepsModal, setShowFirstStepsModal] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [baseUrlDebug, setBaseUrlDebug] = useState(null);
  const [showDebugBanner, setShowDebugBanner] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMemberStats();
    checkOnboardingStatus();
    fetchBaseUrlDebug();
    
    // Check if user needs first steps guidance
    const hasSeenFirstSteps = localStorage.getItem(`first_steps_${user?.id}`);
    if (!hasSeenFirstSteps && user) {
      // Show modal if user hasn't completed essential steps
      const needsSetup = !user?.tron_wallet_address || !user?.full_name;
      if (needsSetup) {
        setShowFirstStepsModal(true);
      }
    }
  }, [user]);

  // Fetch base URL debug info for verification
  const fetchBaseUrlDebug = async () => {
    try {
      const response = await axios.get(`${API}/debug/base-url`);
      setBaseUrlDebug(response.data?.base_url_debug);
    } catch (error) {
      console.error('Failed to fetch base URL debug info:', error);
    }
  };

  const fetchMemberStats = async () => {
    try {
      const response = await axios.get(`${API}/member/dashboard`);
      setMemberStats(response.data);
    } catch (error) {
      console.error('Failed to fetch member stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/member/onboarding/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.ok) {
        // Show onboarding wizard if not completed
        if (!data.progress.completed) {
          setShowOnboardingWizard(true);
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const handleOnboardingComplete = () => {
    toast.success('🎉 Rookie onboarding complete! Welcome to Boot Camp!');
    setShowOnboardingWizard(false);
  };

  // Universal copy to clipboard function with multiple fallbacks
  const copyToClipboard = async (text, successMessage = 'Copied to clipboard!') => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(successMessage);
        return true;
      } catch (err) {
        console.log('Clipboard API failed, trying fallback...');
      }
    }
    
    // Fallback 1: execCommand with textarea
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.setAttribute('readonly', '');
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(successMessage);
        return true;
      }
    } catch (err) {
      console.log('execCommand fallback failed...');
    }
    
    // Fallback 2: Show manual copy prompt with the full link visible
    toast.error(
      <div className="space-y-2">
        <p>Could not auto-copy. Please copy manually:</p>
        <p className="font-mono text-xs bg-gray-100 p-2 rounded break-all select-all">{text}</p>
      </div>,
      { duration: 10000 }
    );
    return false;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free':
        return 'bg-gray-100 text-gray-800';
      case 'rookie_pro':
        return 'bg-blue-100 text-blue-800';
      case 'pro':
        return 'bg-purple-100 text-purple-800';
      case 'enterprise':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'enterprise':
        return <Crown className="h-4 w-4" />;
      case 'pro':
        return <Star className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  // Progressive Quick Actions based on user progress
  const getQuickActions = () => {
    const baseActions = [
      {
        title: "Boot Camp",
        description: "Complete your training & unlock Phase 2",
        icon: GraduationCap,
        color: "bg-yellow-500",
        action: () => navigate('/bootcamp'),
        available: true,
        priority: 1
      },
      {
        title: "My Profile",
        description: "Manage your business profile & settings",
        icon: User,
        color: "bg-indigo-500",
        action: () => navigate('/profile'),
        available: true,
        priority: 2
      },
      {
        title: "Buy Credits",
        description: "Purchase credits for marketplace products",
        icon: CreditCard,
        color: "bg-green-500",
        action: () => navigate('/credits'),
        available: true,
        priority: 3
      },
      {
        title: "Buy PIF Voucher",
        description: "Purchase voucher to sponsor new members",
        icon: Gift,
        color: "bg-blue-500",
        action: () => navigate('/credits?section=pif'),
        available: true,
        priority: 4
      }
    ];

    // Phase 2 Actions (unlocked after 12 PIF sponsors)
    const phase2Actions = [
      {
        title: "Marketplace",
        description: "Browse & download digital products",
        icon: ShoppingBag,
        color: "bg-purple-500",
        action: () => navigate('/marketplace'),
        available: memberStats?.phase_2_unlocked || false,
        priority: 5,
        lockReason: "Sponsor 12 members to unlock"
      },
      {
        title: "Rookie Pro Credits",
        description: "Advanced credit packages & features",
        icon: Star,
        color: "bg-yellow-600",
        action: () => navigate('/credits?tier=rookie_pro'),
        available: memberStats?.phase_2_unlocked || false,
        priority: 6,
        lockReason: "Sponsor 12 members to unlock"
      },
      {
        title: "Pro Credits",
        description: "Professional tier credits & tools",
        icon: Crown,
        color: "bg-purple-600",
        action: () => navigate('/credits?tier=pro'),
        available: (memberStats?.total_pif_sponsored || 0) >= 144,
        priority: 7,
        lockReason: "Sponsor 144 members to unlock"
      },
      {
        title: "Page Builder",
        description: "Create pages from professional templates",
        icon: Globe,
        color: "bg-blue-600",
        action: () => navigate('/pages'),
        available: memberStats?.phase_2_unlocked || false,
        priority: 8,
        lockReason: "Sponsor 12 members to unlock"
      }
    ];

    return [...baseActions, ...phase2Actions].sort((a, b) => a.priority - b.priority);
  };

  const quickActions = getQuickActions();

  const tools = [
    {
      title: "Page Builder",
      description: "Create splash and landing pages",
      icon: Globe,
      status: memberStats?.phase_2_unlocked ? "Available" : "Phase 2 Required",
      available: memberStats?.phase_2_unlocked || false,
      action: () => navigate('/pages'),
      launchDate: null // Available based on user progress
    },
    {
      title: "Link-in-Bio",
      description: "Custom bio link pages",
      icon: LinkIcon,
      status: "Available Now",
      available: true,
      action: () => navigate('/link-in-bio'),
      launchDate: null // Already launched
    },
    {
      title: "URL Rotator", 
      description: "Split test your links",
      icon: RotateCcw,
      status: "Launching Dec 15, 2024",
      available: false,
      action: () => toast.info('URL Rotator launches December 15, 2024'),
      launchDate: new Date('2024-12-15')
    },
    {
      title: "CRM Lite",
      description: "Manage your contacts",
      icon: Users,
      status: "Launching Jan 1, 2025", 
      available: false,
      action: () => toast.info('CRM Lite launches January 1, 2025'),
      launchDate: new Date('2025-01-01')
    }
  ];

  const handleFirstStepsClose = () => {
    setShowFirstStepsModal(false);
    localStorage.setItem(`first_steps_${user?.id}`, 'seen');
  };

  const handleFirstStepsComplete = (stepId) => {
    // Handle step completion - could update backend here
    console.log(`Step ${stepId} completed`);
    if (stepId === 1) {
      // Wallet step completed - could refresh member stats
      fetchMemberStats();
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-30 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center font-bold text-black">
                BBC
              </div>
              <div className="ml-3">
                <div className="font-semibold text-gray-900">Quick List</div>
                <div className="text-xs text-gray-500">Member Portal</div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Dashboard
              </div>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg bg-gray-100">
                <TrendingUp className="h-5 w-5 mr-3" />
                Overview
              </a>
            </div>

            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Tools
              </div>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <Globe className="h-5 w-5 mr-3" />
                Page Builder
              </a>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <LinkIcon className="h-5 w-5 mr-3" />
                Link-in-Bio
              </a>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <CreditCard className="h-5 w-5 mr-3" />
                Ads Manager
              </a>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <Users className="h-5 w-5 mr-3" />
                CRM
              </a>
            </div>

            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Growth
              </div>
              <a href="/pif" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <Gift className="h-5 w-5 mr-3" />
                Pay It Forward
              </a>
            </div>

            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Account
              </div>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <Wallet className="h-5 w-5 mr-3" />
                Wallet
              </a>
              <a href="#" className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                <Settings className="h-5 w-5 mr-3" />
                Settings
              </a>
            </div>
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center font-bold text-black">
                {user?.full_name?.charAt(0) || 'M'}
              </div>
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium text-gray-900">{user?.full_name}</div>
                <div className="flex items-center space-x-2">
                  <Badge className={getTierColor(memberStats?.membership_tier)}>
                    {getTierIcon(memberStats?.membership_tier)}
                    <span className="ml-1 capitalize">
                      {memberStats?.membership_tier?.replace('_', ' ') || 'Free'}
                    </span>
                  </Badge>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-gray-600 hover:text-gray-900"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden mr-4"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Welcome back, {user?.full_name?.split(' ')[0]}!
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-gray-600">
                    {memberStats?.founder_title || 'Founder'} •
                  </p>
                  <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black">
                    <Crown className="h-3 w-3 mr-1" />
                    {memberStats?.rank || 'Starter'}
                  </Badge>
                  <p className="text-gray-500 text-sm">
                    Since {memberStats ? new Date(memberStats.member_since).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className={getTierColor(memberStats?.membership_tier)}>
                {getTierIcon(memberStats?.membership_tier)}
                <span className="ml-1 capitalize">
                  {memberStats?.membership_tier?.replace('_', ' ') || 'Free'} Member
                </span>
              </Badge>
            </div>
          </div>
        </header>

        {/* Debug Banner - Shows detected base URL for verification */}
        {showDebugBanner && baseUrlDebug && (
          <div className="bg-yellow-100 border-b border-yellow-300 px-6 py-2">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium text-yellow-800">Base URL Detected: </span>
                <span className="font-mono text-yellow-900">{baseUrlDebug.resolved_base_url}</span>
                {baseUrlDebug.FRONTEND_URL_valid && (
                  <span className="ml-2 text-green-700 text-xs">(✓ Valid)</span>
                )}
              </div>
              <button 
                onClick={() => setShowDebugBanner(false)}
                className="text-yellow-700 hover:text-yellow-900 text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        <main className="p-6">
          {/* P1.5 — What you'll unlock next */}
          <div className="mb-6">
            <TierProgressionWidget />
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Total Earnings
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${memberStats?.total_earnings?.toFixed(2) || '0.00'}
                </div>
                <p className="text-xs text-gray-500 mt-1">Lifetime earnings</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Missed Earnings
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  -${memberStats?.missed_earnings?.toFixed(2) || '0.00'}
                </div>
                <p className="text-xs text-red-500 mt-1">Lost to inactivity</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Credit Balance
                </CardTitle>
                <CreditCard className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {memberStats?.credit_balance || 0}
                </div>
                <p className="text-xs text-gray-500 mt-1">Available credits</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  PIF Sponsored
                </CardTitle>
                <Gift className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {memberStats?.total_pif_sponsored || 0}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {memberStats?.phase_2_unlocked ? 'Phase 2 Unlocked!' : `${12 - (memberStats?.total_pif_sponsored || 0)} to Phase 2`}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Your Referral Link
                </CardTitle>
                <LinkIcon className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div 
                  className="text-xs font-mono bg-gray-50 p-2 rounded border mb-2 truncate select-all cursor-pointer"
                  onClick={(e) => e.target.select && e.target.select()}
                  title="Click to select, or use copy button"
                >
                  {memberStats?.referral_link || `${PUBLIC_URL}/?ref=${user?.id}`}
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full text-xs"
                  data-testid="copy-referral-link-btn"
                  onClick={() => {
                    const link = memberStats?.referral_link || `${PUBLIC_URL}/?ref=${user?.id}`;
                    copyToClipboard(link, 'Referral link copied!');
                  }}
                >
                  Copy Link
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Quick Action
                </CardTitle>
                <Plus className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <Button className="w-full btn-primary">
                  Buy Credits
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Missed Cycles Alert */}
          {memberStats?.missed_cycles > 0 && (
            <Card className="mb-8 border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <TrendingUp className="h-5 w-5 text-red-500" />
                  Missed Earning Opportunities
                </CardTitle>
                <CardDescription className="text-red-600">
                  You've missed {memberStats?.missed_cycles || 0} payment cycles due to inactivity
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-700">
                      Last missed cycle: ${memberStats?.last_missed_amount?.toFixed(2) || '5.95'}
                    </span>
                    <Badge variant="destructive">
                      Redirected to sponsor
                    </Badge>
                  </div>
                  
                  <div className="p-3 bg-red-100 rounded-lg border border-red-200">
                    <div className="flex items-start gap-2">
                      <Gift className="h-4 w-4 text-red-600 mt-0.5" />
                      <div className="text-sm">
                        <div className="font-medium text-red-800">
                          Start paying it forward to reactivate earnings
                        </div>
                        <div className="text-red-700 mt-1">
                          After {memberStats?.payment_count >= 4 ? '4 payments' : `${4 - (memberStats?.payment_count || 0)} more payments`}, 
                          you must sponsor someone to continue receiving earnings.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Referral Link Prominent Display */}
          <Card className="mb-8 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <LinkIcon className="h-5 w-5 text-blue-500" />
                Your Founder Referral Link
              </CardTitle>
              <CardDescription className="text-blue-600">
                Share this link to invite new members and earn commissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div 
                    className="flex-1 p-3 bg-white border border-blue-200 rounded-lg font-mono text-sm select-all cursor-pointer"
                    onClick={(e) => {
                      // Allow text selection on click
                      if (e.target.setSelectionRange) {
                        e.target.setSelectionRange(0, e.target.value?.length || e.target.textContent?.length);
                      }
                      window.getSelection()?.selectAllChildren(e.target);
                    }}
                    title="Click to select, or use copy button"
                    data-testid="referral-link-display"
                  >
                    {memberStats?.referral_link || `${PUBLIC_URL}/?ref=${user?.id}`}
                  </div>
                  <Button 
                    className="btn-primary"
                    data-testid="copy-referral-link-prominent-btn"
                    onClick={() => {
                      const link = memberStats?.referral_link || `${PUBLIC_URL}/?ref=${user?.id}`;
                      copyToClipboard(link, 'Referral link copied to clipboard!');
                    }}
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-2 bg-white rounded border border-blue-200">
                    <div className="font-medium text-blue-800">Referrals This Month</div>
                    <div className="text-blue-600">{memberStats?.monthly_referrals || 0} people</div>
                  </div>
                  <div className="p-2 bg-white rounded border border-blue-200">
                    <div className="font-medium text-blue-800">Commission Rate</div>
                    <div className="text-blue-600">${memberStats?.commission_rate || '5.95'} per referral</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PIF Progress Card */}
          {!loading && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-yellow-500" />
                  Pay It Forward Progress
                </CardTitle>
                <CardDescription>
                  {memberStats?.next_milestone || "Track your sponsorship progress"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Members Sponsored: {memberStats?.total_pif_sponsored || 0}
                    </span>
                    <Badge 
                      variant={memberStats?.phase_2_unlocked ? "default" : "secondary"}
                      className={memberStats?.phase_2_unlocked ? "bg-green-500" : ""}
                    >
                      {memberStats?.phase_2_unlocked ? "Phase 2 Unlocked!" : "Phase 1"}
                    </Badge>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${memberStats?.progress_percentage || 0}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Current: {memberStats?.total_pif_sponsored || 0}</span>
                    <span>{memberStats?.progress_percentage || 0}% Complete</span>
                    <span>
                      Next: {(memberStats?.total_pif_sponsored || 0) < 12 ? '12' : '144'}
                    </span>
                  </div>
                  
                  {!memberStats?.phase_2_unlocked && (
                    <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-start gap-2">
                        <Crown className="h-4 w-4 text-yellow-600 mt-0.5" />
                        <div className="text-sm">
                          <div className="font-medium text-yellow-800">
                            Unlock Phase 2 Benefits
                          </div>
                          <div className="text-yellow-700 mt-1">
                            Sponsor {12 - (memberStats?.total_pif_sponsored || 0)} more members to unlock:
                            Marketplace, Advanced Credits, Page Builder, and more!
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>
                  Common tasks to get you started
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  const isLocked = !action.available;
                  
                  return (
                    <div key={index} className="relative">
                      <button
                        onClick={isLocked ? undefined : action.action}
                        disabled={isLocked}
                        className={`w-full flex items-center p-3 rounded-lg transition-colors text-left ${
                          isLocked 
                            ? 'bg-gray-100 cursor-not-allowed opacity-60' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        title={isLocked ? action.lockReason : action.description}
                      >
                        <div className={`w-10 h-10 ${isLocked ? 'bg-gray-400' : action.color} rounded-lg flex items-center justify-center mr-3`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
                            {action.title}
                            {isLocked && <span className="ml-2 text-xs">🔒</span>}
                          </div>
                          <div className={`text-sm ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                            {isLocked ? action.lockReason : action.description}
                          </div>
                        </div>
                        {isLocked && (
                          <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-600 border-yellow-200">
                            Locked
                          </Badge>
                        )}
                      </button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Available Tools</CardTitle>
                <CardDescription>
                  Marketing tools for your campaigns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {tools.map((tool, index) => {
                  const Icon = tool.icon;
                  const isAvailable = tool.available;
                  const isLaunched = !tool.launchDate || new Date() >= tool.launchDate;
                  
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isAvailable 
                          ? 'bg-green-50 hover:bg-green-100 cursor-pointer border border-green-200' 
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                      onClick={isAvailable ? tool.action : undefined}
                    >
                      <div className="flex items-center">
                        <Icon className={`h-5 w-5 mr-3 ${
                          isAvailable ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <div>
                          <div className={`font-medium ${
                            isAvailable ? 'text-gray-900' : 'text-gray-600'
                          }`}>
                            {tool.title}
                          </div>
                          <div className="text-sm text-gray-500">{tool.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isAvailable ? (
                          <Badge className="text-xs bg-green-100 text-green-800 border-green-300">
                            ✅ {tool.status}
                          </Badge>
                        ) : tool.launchDate && new Date() < tool.launchDate ? (
                          <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-600 border-yellow-300">
                            🕒 {tool.status}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-300">
                            🔒 {tool.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Game Integration */}
            <Card className="border-purple-200">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <Crown className="h-5 w-5" />
                  BBC Game Arena
                </CardTitle>
                <CardDescription className="text-purple-600">
                  Use your credits to play games and win prizes
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Game Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-800">
                      {memberStats?.game_credits || 0}
                    </div>
                    <div className="text-xs text-purple-600">Game Credits</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-800">
                      {memberStats?.game_pieces || 0}
                    </div>
                    <div className="text-xs text-purple-600">Game Pieces</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-800">
                      {memberStats?.total_wins || 0}
                    </div>
                    <div className="text-xs text-purple-600">Total Wins</div>
                  </div>
                </div>

                {/* Credit Transfer */}
                <div className="p-4 border border-purple-200 rounded-lg bg-purple-25">
                  <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Transfer Credits to Game
                  </h4>
                  <p className="text-sm text-purple-600 mb-3">
                    Transfer your BBC credits to game credits for playing games and purchasing power-ups
                  </p>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      placeholder="Amount"
                      className="flex-1 px-3 py-2 border border-purple-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <Button 
                      size="sm" 
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => toast.info('Game credit transfer coming soon!')}
                    >
                      Transfer
                    </Button>
                  </div>
                  <div className="flex justify-between text-xs text-purple-600 mt-2">
                    <span>Available: {memberStats?.credit_balance || 0} credits</span>
                    <span>Rate: 1:1 transfer</span>
                  </div>
                </div>

                {/* Game Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={() => toast.info('Game Arena launching Q1 2025!')}
                  >
                    🎮 Play Games
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={() => toast.info('Game Store launching Q1 2025!')}
                  >
                    🛒 Game Store
                  </Button>
                </div>

                <div className="text-center text-xs text-purple-500 bg-purple-25 p-2 rounded">
                  🚀 Game Arena launching Q1 2025 - Transfer credits now to be ready!
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Getting Started */}
          <Card>
            <CardHeader>
              <CardTitle>Getting Started Checklist</CardTitle>
              <CardDescription>
                Complete these steps to maximize your success
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-gray-900">Account created and verified</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                  <span className="text-gray-500">Complete your profile</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                  <span className="text-gray-500">Purchase your first credit pack</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                  <span className="text-gray-500">Create your first advertisement</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                  <span className="text-gray-500">Build your landing page</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
      
      {/* First Steps Modal */}
      {showFirstStepsModal && (
        <FirstStepsModal 
          user={user}
          onClose={handleFirstStepsClose}
          onStepComplete={handleFirstStepsComplete}
        />
      )}
      
      {/* Rookie Onboarding Wizard */}
      <OnboardingWizard
        isOpen={showOnboardingWizard}
        onClose={() => setShowOnboardingWizard(false)}
        onComplete={handleOnboardingComplete}
      />
    </div>
  );
};


export default MemberDashboard;